/**
 * 每维结论（2026-09-16 拍板：**B 档**，原 C 档作废）
 *
 * B 档 = 允许引外部参考值、允许描述性评价。底线只留一条：
 * **不做医学诊断、不编造查不到出处的阈值** —— 所以 GUIDELINE 里只有运动与睡眠（WHO / AASM），
 * 饮食与心理只给事实与自己的趋势，不会为了“看起来完整”造一个阈值。
 *
 * 结论的来源，按可信度排序，最多取两句：
 *  ① 外部参考值（运动：周均分钟 vs 150；睡眠：日均小时 vs 7–9）
 *  ② 运动时段偏好（`features.active*Times` 的分布读成一句话）
 *  ③ 该用户自己的基线（`baseline.source === 'individual'`，P25–P75）
 *  ④ 该用户自己的趋势（服务端的 `features.slopePerWeek`，不是前端拟合的）
 *
 * 2026-09-17 的两处调整（运营试用后提）：
 *  · **自设目标那一句搬走了**：它是数字，现在长在卡片的“目标进度”行里
 *    （每周目标 4 次 · 实际 1.2 次 · 差 2.8 次），不再占一行结论；
 *  · **兜底的“N 天有记录、最长连续 M 天”删掉了**：那正是卡片三个大号数字里的两个，
 *    写在下面只是把同一件事说两遍。整维没记录时改说一句直白的话（`notFormed`）。
 *
 * ⚠️ 本文件是 R16（呈现层不做数值判定）的**登记过的例外**，产品拍板要的：
 * 比的是**用户自己的**目标/基线、或**有出处的**外部参考，判据（够不够、给不给分）仍只来自服务端
 * `buckets`/`score`。因此它不在 buildChecks 的 R16 扫描名单里 —— 是例外，不是漏网。
 *
 * 句式全在 copy.js（那边会被禁词扫描扫到；禁词表已于 2026-09-16 按拍板废弃，但结构保留）。
 */
import { CONCLUSION_COPY, GUIDELINE, formatNumber, num } from './copy';
import { PREF_SLOTS, PRIMARY_METRIC, isDimensionEmpty } from './registry';

const WEEK_DAYS = 7;
/** 结论最多两句：卡片要的是“一眼看到洞察”，不是一段分析报告 */
const MAX_LINES = 2;

/**
 * “整维没有记录”时那句话里的名词。**只给饮食与心理**：
 * 这两维靠用户自己打卡，说“尚未形成记录习惯”是准确的；
 * 睡眠是设备同步（没数据往往是没戴设备、不是没习惯），运动也另有桶码说清为什么，都不套这句。
 */
const NO_RECORD_NOUN = { DIET: '饮食', MENTAL: '心情' };

/** 窗口周数。用窗口天数算而不是写死 6：窗口以后若改，这里跟着走。 */
function weeksOf(windowDays) {
  const days = num(windowDays);
  if (days === null || days <= 0) return null;
  return days / WEEK_DAYS;
}

/** 三态比较：1 高 / 0 相等 / -1 低；缺值返回 null（调用方据此不写这一句） */
function compare(value, reference) {
  if (value === null || reference === null || reference === undefined) {
    return null;
  }
  if (value > reference) return 1;
  if (value < reference) return -1;
  return 0;
}

/** 该维的主指标总量（走 facts；没有对应事实行时返回 null，不自己加总） */
function primaryTotal(dim, metric) {
  if (!metric || !metric.factKey) return null;
  const facts = Array.isArray(dim && dim.facts) ? dim.facts : [];
  const hit = facts.find(
    (fact) => fact && fact.key === metric.factKey && fact.observed !== false,
  );
  return hit ? num(hit.value) : null;
}

/** ① 外部参考值那一句（只有运动与睡眠有出处） */
function guidelineLine(dimension, dim, weeks) {
  const metric = PRIMARY_METRIC[dimension] || {};
  const guide = GUIDELINE[dimension];
  if (dimension === 'SPORT' && guide && weeks) {
    const total = primaryTotal(dim, metric);
    if (total === null) return null;
    const weekly = total / weeks;
    const text = CONCLUSION_COPY.sportGuide(formatNumber(weekly));
    return compare(weekly, guide.weeklyMinutes) === -1
      ? `${text}本次低于该参考值。`
      : `${text}本次达到或超过该参考值。`;
  }
  if (dimension === 'SLEEP' && guide) {
    const mean = num((dim.features || {}).mean);
    if (mean === null) return null;
    const text = CONCLUSION_COPY.sleepGuide(formatNumber(mean));
    if (mean < guide.minHours) return `${text}本次低于该区间。`;
    if (mean > guide.maxHours) return `${text}本次高于该区间。`;
    return `${text}本次落在该区间内。`;
  }
  return null;
}

/**
 * ② 运动时段偏好（2026-09-16 新增，替代卡片里那四个次数格）。
 *
 * 判据只有两条、都是描述性的：
 *  · `prefEntropy` 接近 1 ⇒ 四个时段几乎平均 ⇒ 说“分布较均匀”才是诚实的（别硬点名一个“偏好”）；
 *  · 否则按占比给“集中在 / 略偏向”。
 * 服务端没算时段（`active*Times` 全为 null，如 sit-like）时返回 null，一句都不说 ——
 * 这比拿 0 画出一个“偏好夜间”的假结论强。
 */
function prefLine(features) {
  const known = PREF_SLOTS.map((slot) => ({
    short: slot.short,
    value: num(features[slot.key]),
  })).filter((slot) => slot.value !== null);
  if (!known.length) return null;
  const total = known.reduce((acc, slot) => acc + slot.value, 0);
  if (total <= 0) return null;
  const top = known.reduce((a, b) => (b.value > a.value ? b : a));
  const pct = formatPercent(top.value / total);
  const entropy = num(features.prefEntropy);
  if (entropy !== null && entropy >= 0.9) {
    return CONCLUSION_COPY.prefEven(top.short, pct);
  }
  if (top.value / total < 0.4) {
    return CONCLUSION_COPY.prefLeaning(top.short, pct);
  }
  return CONCLUSION_COPY.prefDominant(top.short, pct);
}

function formatPercent(ratio) {
  return `${Math.round(ratio * 100)}%`;
}

/**
 * 一维的结论（最多两句）。整维没有记录时：
 *  · 饮食/心理 → 一句“尚未形成记录习惯”；
 *  · 其他维 → 空数组（“为什么没有”由卡片里的桶码提示说，结论不重复说一遍）。
 *
 * @param {string} dimension SPORT / SLEEP / DIET / MENTAL
 * @param {object} dim 该维载荷
 * @param {number|string} windowDays 观察窗天数
 * @returns {string[]}
 */
export function conclusionsFor(dimension, dim, windowDays) {
  if (!dim) return [];

  const features = dim.features || {};
  const empty = isDimensionEmpty(dim) || num(features.activeDays) === 0;
  if (empty) {
    const noun = NO_RECORD_NOUN[dimension];
    return noun ? [CONCLUSION_COPY.notFormed(noun)] : [];
  }

  const baseline = dim.baseline || {};
  const metric = PRIMARY_METRIC[dimension] || {};
  const unit = metric.unit || '';
  const weeks = weeksOf(windowDays);
  const out = [];

  // ① 外部参考值
  const guide = guidelineLine(dimension, dim, weeks);
  if (guide) out.push(guide);

  // ② 运动时段偏好
  if (dimension === 'SPORT') {
    const pref = prefLine(features);
    if (pref) out.push(pref);
  }

  // ③ 自身基线（none 绝不回落指南值，也就不出这一句）
  const mean = num(features.mean);
  const p25 = num(baseline.p25);
  const p75 = num(baseline.p75);
  if (
    out.length < MAX_LINES &&
    baseline.source === 'individual' &&
    mean !== null &&
    p25 !== null &&
    p75 !== null
  ) {
    const args = [
      formatNumber(mean),
      unit,
      formatNumber(p25),
      formatNumber(p75),
    ];
    if (mean > p75) out.push(CONCLUSION_COPY.baselineOver(...args));
    else if (mean < p25) out.push(CONCLUSION_COPY.baselineUnder(...args));
    else out.push(CONCLUSION_COPY.baselineIn(...args));
  }

  // ④ 趋势（服务端给的 slopePerWeek：前端不拟合，只读数）
  const slope = num(features.slopePerWeek);
  if (slope !== null && out.length < MAX_LINES) {
    if (slope > 0) out.push(CONCLUSION_COPY.trendUp(formatNumber(slope)));
    else if (slope < 0)
      out.push(CONCLUSION_COPY.trendDown(formatNumber(slope)));
    else out.push(CONCLUSION_COPY.trendFlat);
  }

  return out.slice(0, MAX_LINES);
}
